/* eslint-disable @typescript-eslint/indent */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Loader, styles, SdirModal, AlertSingleLine } from '@sdir/sds';
import { useIntl } from 'react-intl';
import moment from 'moment';
import {
  ApplicationType,
  ApplicationProcessType,
  V1CreateapplicationCreateownapplicationCommandPostRequest as CreateApplicationCommand,
} from '@sdir/httpclient/lib/clients/aps/application';
import { generatePath, useLocation } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import {
  createApplicationApi,
  getApplicationApi,
  personalCertificateApi,
} from '../../../../httpclient';
import { ApplicationLayout } from '../../ApplicationLayout';
import { FormSelect } from '../../../Molecules';
import FormRadioButtonGroup, {
  RadioOnChange,
  RadioOption,
} from '../../../Molecules/FormRadioButtonGroup';
import {
  GetLocalizedCertificateName,
  GetLocalizedStringFromLanguageComponent,
  translateCertificateType,
} from '../../../../helpers/Certificates';
import { LoadWrapper } from '../../../Atoms';
import {
  useCertificateCodes,
  useFeatureToggles,
  useCurrentPerson,
  useCurrentUserInfo,
} from '../../../../services/hooks';
import { SelectOptions } from '../../../Molecules/FormSelect';
import { useOwnCertificates } from '../../../../services/hooks/useOwnCertificates';
import PhysicalCertificate from '../../../Atoms/PhysicalCertificate';
import { getStepsForAppType } from '../../../../helpers/Steps';
import { ToggleContent } from '../../../Atoms/ToggleContent';
import {
  isToggleOn,
  ToggleDisablePrinting,
  ToggleAvailableCertificates,
  getAvailableCertificates,
} from '../../../../constants/permissions';
import { isOpenApplication } from '../../../../helpers/applicationHelpers';
import { routes } from '../../../../constants/routes';
import { useNavigateWithLang } from '../../../../services/hooks/useNavigateWithLang';

type CertificateDetails = {
  certificateCode?: string | null;
  type?: ApplicationType | null;
};

const SelectCertificate: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const location = useLocation();
  const featureToggle = useFeatureToggles();
  const {
    userInfo: { isExternal },
  } = useCurrentUserInfo();

  const [physicalCertChecked, setPhysicalCertChecked] = useState(false);
  const retryTimeout = useRef<any>(null);

  const {
    person: { loading: loadingTmp, result: resultTmp },
  } = useCurrentPerson();

  const {
    result: ownAppResult,
    loading: ownAppLoading,
    errorObject: ownAppError,
  } = useApi({
    apiFunction: () =>
      getApplicationApi.v1GetGetownapplicationsQueryPost({
        filter: {
          status: undefined,
          freeTextSearchStringOnType: undefined,
          applicationTypes: undefined,
        },
      }),
    callApiOnInit: true,
  });

  const { result: ownRenewals, loading: ownRenewalsLoading } = useApi({
    apiFunction: () => personalCertificateApi.v1PersonalcertificateGetownavailablerenewalsGet(),
    callApiOnInit: true,
  });

  const openApplicationsCertificate = useMemo(() => {
    if (!ownAppResult) return [];

    return ownAppResult.filter((app) => isOpenApplication(app.applicationStatus || ''));
  }, [ownAppResult]);

  const isAvailableCertificatesToggleOn = isToggleOn(
    featureToggle?.result,
    ToggleAvailableCertificates,
    resultTmp?.personalIdentificationNumber
  );

  const { certificateCodes: availableCertificateCodes, userOverride: userOverrideCertificates } =
    getAvailableCertificates(
      featureToggle?.result,
      ToggleAvailableCertificates,
      resultTmp?.personalIdentificationNumber
    );

  const { predefinedType, predefinedCertificateCode } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const locationAppType = (searchParams.get('type') as ApplicationType | undefined) || null;
    const predefinedCertificate = searchParams.get('certificate') || null;
    return {
      predefinedType: locationAppType || null,
      predefinedCertificateCode: predefinedCertificate || null,
    };
  }, [location.search]);

  const {
    ownCertificates: { result: certResult, loading: certLoading },
  } = useOwnCertificates();

  const initialValues: CertificateDetails = {
    certificateCode: predefinedCertificateCode,
    type: predefinedType,
  };

  const [apiCallError, setApiCallError] = useState<Error | null>(null);
  const [apiCallWarning, setApiCallWarning] = useState<string | null>(null);
  const [openApplicationExistUid, setOpenApplicationExistUid] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationType, setApplicationType] = useState<ApplicationType>(() => {
    return initialValues.type || ApplicationType.ApplicationTypeNotDefined;
  });

  const codes = useCertificateCodes();

  useEffect(() => {
    return () => {
      if (retryTimeout?.current) {
        clearTimeout(retryTimeout.current);
      }
    };
  }, []);

  const radioGroupOptions = useMemo(() => {
    const types = [ApplicationType.FirstTime, ApplicationType.Renewal];
    return types.map<RadioOption>((t) => ({
      label: translateCertificateType(t, intl),
      value: t,
    }));
  }, [intl]);

  const certificateCodes = useMemo(() => {
    if (!codes.result?.length || applicationType === ApplicationType.ApplicationTypeNotDefined) {
      return [];
    }

    let options: SelectOptions<string>[] = [];

    const filterToggledCertificates = (code: string) => {
      if (isAvailableCertificatesToggleOn) {
        if (userOverrideCertificates) return true;
        if (availableCertificateCodes?.includes(code)) return true;
        return false;
      }
      return false;
    };

    const formatSelectOption = (name: string, code?: string | null) => ({
      name: name || '<no description>',
      value: code || '',
      secondValue: code?.split('_')[0],
    });

    if (applicationType === ApplicationType.Renewal) {
      options = (ownRenewals || [])
        .filter((r) => {
          const code = r?.certificateCode || '';
          return filterToggledCertificates(code);
        })
        .map<SelectOptions<string>>((v) => {
          const name = GetLocalizedStringFromLanguageComponent(
            intl.locale,
            v?.certificateName || undefined
          );
          return formatSelectOption(name, v?.certificateCode);
        });
    } else {
      options = codes?.result
        .filter((r) => {
          const [code, type] = r?.code?.split('_') as string[];
          return filterToggledCertificates(code);
        })
        .filter((c) => {
          const [code, type] = c?.code?.split('_') as string[];
          return type
            ? type === applicationType.toLowerCase()
            : applicationType === ApplicationType.FirstTime;
        })
        .map<SelectOptions<string>>((v) => {
          const name = GetLocalizedCertificateName(v);
          return formatSelectOption(name, v?.code);
        });
    }

    return options.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
  }, [
    applicationType,
    certResult,
    intl.locale,
    codes,
    ownRenewals,
    isAvailableCertificatesToggleOn,
    userOverrideCertificates,
    availableCertificateCodes,
  ]);

  const handleApplicationTypeChange = useCallback(
    (e: RadioOnChange) => {
      const type = ApplicationType[e.target.value];
      if (type !== applicationType) setApplicationType(type);
    },
    [applicationType]
  );

  const isCertificatePrintable = useCallback(
    (values) => {
      const certificate = codes?.result?.find((cert) => cert.code === values.certificateCode);
      return certificate?.isPrintable;
    },
    [codes?.result]
  );

  const handleSubmit = (values: CertificateDetails) => {
    setOpenApplicationExistUid('');
    if (ownAppLoading) return;

    const existingOpenApplication = openApplicationsCertificate.find(
      (openApp) => openApp.certificate === values.certificateCode
    );

    if (existingOpenApplication) {
      setOpenApplicationExistUid(existingOpenApplication.uid || '');
      return;
    }

    setApiCallError(null);
    setApiCallWarning(null);
    setIsSubmitting(true);
    const NewApplication: CreateApplicationCommand = {
      applicationProcessType: ApplicationProcessType.Na,
      certificateCode: values.certificateCode,
      applicationType: values.type || undefined,
      applicationDate: moment(new Date()).format('YYYY-MM-DDTHH:mm').toString(),
      printCertificate: physicalCertChecked,
    };

    createApplicationApi
      .v1CreateapplicationCreateownapplicationCommandPost(NewApplication)
      .then((r) => {
        const uid = r.data.uid as string;
        const firstStep = getStepsForAppType(
          values.type || ApplicationType.FirstTime,
          isExternal
        )[0];
        navigate(generatePath(firstStep.route, { applicationUid: uid }));
      })
      .catch((e: Error) => {
        setApiCallError(e);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        certificateCode: Yup.string()
          .required(intl.formatMessage({ id: 'general.form.error' }))
          .nullable(),
        type: Yup.string()
          .required(intl.formatMessage({ id: 'general.form.error' }))
          .nullable(),
      }),
    [intl]
  );

  const handleNavigateToOpenApplication = () => {
    const path = generatePath(routes.application.details, {
      applicationUid: openApplicationExistUid || '',
    });
    navigate(path);
  };

  if ((codes.loading || certLoading || ownRenewalsLoading) && initialValues.certificateCode) {
    return <Loader />;
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ submitForm, values }) => (
          <Form noValidate>
            <ApplicationLayout
              topHeading={intl.formatMessage({ id: 'select.certificate.h1' })}
              heading=""
              exitLabel={intl.formatMessage({ id: 'general.form.back' })}
              mainContentMaxWidth="100%"
              canNavBack
              canNavNext={!isSubmitting}
              navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
              navNextLabel={intl.formatMessage({ id: 'general.form.next' })}
              onNavNextClick={submitForm}
              onNavBackClick={() => navigate(-1)}
            >
              <Wrapper>
                <InnerWrapper>
                  <H2>
                    {intl.formatMessage({
                      id: 'select.certificate.title',
                    })}
                  </H2>

                  <AppType>
                    <Label htmlFor="appType">
                      {intl.formatMessage({
                        id: 'certificate.app.type',
                      })}
                    </Label>
                    <RadioButtonDiv>
                      <Field
                        name="type"
                        radioContainer={CustomContainer}
                        options={radioGroupOptions}
                        component={FormRadioButtonGroup}
                        onChange={handleApplicationTypeChange}
                      />
                    </RadioButtonDiv>
                    {values?.type === ApplicationType.Renewal && (
                      <RenewalInfo>
                        <AlertSingleLine
                          title={intl.formatMessage({ id: 'certificate.type.renewal.title' })}
                          text={intl.formatMessage({ id: 'certificate.type.renewal.info' })}
                          type="info"
                        />
                      </RenewalInfo>
                    )}
                  </AppType>
                  <CourseInput>
                    <Label htmlFor="search">
                      {intl.formatMessage({
                        id: 'certificate.type',
                      })}
                    </Label>
                    <LoadWrapper
                      loading={codes.loading || certLoading}
                      error={
                        codes.result?.length === 0
                          ? intl.formatMessage({ id: 'general.error.no.results' })
                          : codes.error
                      }
                      errorTitleId="certificates.errors.unabletoloadcertificates"
                    >
                      <Field
                        name="certificateCode"
                        className="search-input"
                        component={FormSelect}
                        options={certificateCodes}
                        emptyResultsContent={intl.formatMessage({ id: 'general.search.nomatches' })}
                        searchable
                        disabled={!values.type || ownAppError}
                        defaultVal={intl.formatMessage({ id: 'select.certificate.title' })}
                      />
                    </LoadWrapper>
                  </CourseInput>
                  {applicationType !== ApplicationType.ApplicationTypeNotDefined &&
                    isCertificatePrintable(values) && (
                      <ToggleContent toggleName={ToggleDisablePrinting} showLoader inverse>
                        <PhysicalCertificate
                          checked={physicalCertChecked}
                          setChecked={setPhysicalCertChecked}
                        />
                      </ToggleContent>
                    )}
                  {apiCallError && (
                    <ErrorMessage
                      title={intl.formatMessage({ id: 'certificate.create.error' })}
                      error={apiCallError}
                    />
                  )}
                  {apiCallWarning && (
                    <AlertSingleLine
                      type="warn"
                      title={intl.formatMessage({ id: 'general.error.title' })}
                      text={apiCallWarning}
                    />
                  )}
                  {ownAppError && (
                    <ErrorMessage
                      title={intl.formatMessage({ id: 'certificate.create.fetch.error' })}
                      error={ownAppError}
                    />
                  )}
                  {isSubmitting && <Loader />}
                  {(loadingTmp || resultTmp) && <></>}
                </InnerWrapper>
              </Wrapper>
            </ApplicationLayout>
          </Form>
        )}
      </Formik>
      <SdirModal
        visible={Boolean(openApplicationExistUid)}
        onCancel={() => setOpenApplicationExistUid('')}
        cancelText={intl.formatMessage({ id: 'general.button.close' })}
        closeButton
        okText={intl.formatMessage({ id: 'certificate.app.exists.button' })}
        onOK={handleNavigateToOpenApplication}
      >
        <ModalHeader>{intl.formatMessage({ id: 'certificate.app.exists.head' })}</ModalHeader>
        {intl.formatMessage({ id: 'certificate.app.exists.text' })}
      </SdirModal>
    </>
  );
};

export default SelectCertificate;
const H2 = styled.h2``;
const ModalHeader = styled.h2`
  margin-top: 0;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  background: white;
  padding: 3rem 5rem;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    flex-direction: column;
  }
`;

const InnerWrapper = styled.div`
  min-width: 420px;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    width: 100%;
    min-width: 0;
  }
`;

const RenewalInfo = styled.div`
  margin: 20px 0;
`;

const CourseInput = styled.div`
  margin-bottom: 3rem;
  max-width: 416px;

  .search-input {
    margin-top: 2rem;
  }
`;

const RadioButtonDiv = styled.div`
  margin: 1rem 0 1rem 0;
  max-width: 416px;
`;
const AppType = styled.div`
  margin-top: 4rem;
`;
const CustomContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  [data-id='base-item'] {
    padding: 1rem;
  }
`;

const Label = styled.label`
  font-size: 1.6rem;
  color: ${styles.colors.font.textTable};
`;
